import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isMobileDevice } from '../utils/deviceDetection';
import DesktopBlocker from './DesktopBlocker';
import MobileBlocker from './MobileBlocker';
import SuspendedBlocker from './SuspendedBlocker';

const SUSPENDED_STATUSES = ['banned', 'blocked'];

const ProtectedRoute = ({ children }) => {
  const { user, loading, profileData } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: '#fff',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    // Redirect to sign in page but save the location they were trying to access
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  const isAdminRoute = location.pathname.startsWith('/admin');

  // Suspended accounts lose access. This gate FAILS OPEN by design: it acts only
  // when the profile has actually loaded AND the status is explicitly banned or
  // blocked, so a slow or failed read can never lock out a legitimate user.
  // Admin routes are exempt — an admin is never suspended through this path, and
  // locking the admin panel on a bad read would remove the means to undo it.
  if (
    !isAdminRoute &&
    profileData?.loaded &&
    SUSPENDED_STATUSES.includes(String(profileData.accountStatus || '').toLowerCase())
  ) {
    return <SuspendedBlocker status={profileData.accountStatus} />;
  }

  // Block mobile access to admin dashboard routes
  if (isAdminRoute && isMobileDevice()) {
    return <MobileBlocker />;
  }

  // Block desktop access to non-admin (user) routes
  if (!isAdminRoute && !isMobileDevice()) {
    return <DesktopBlocker><div /></DesktopBlocker>;
  }

  return children;
};

export default ProtectedRoute;
